
import { useState, useMemo, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { SupabaseNotification } from "@/types/notifications";
import { loadPreferences, savePreferences } from "@/utils/notificationPreferences";
import { useDebounce } from "@/hooks/useDebounce";

/**
 * Hook for filtering and sorting notifications
 */
export const useNotificationsFiltering = (notifications: SupabaseNotification[] = []) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => {
    const fromUrl = searchParams.get("q");
    if (fromUrl !== null) return fromUrl;
    const cached = loadPreferences() as any;
    return cached.searchTerm ?? "";
  });
  
  // Debounce search term to prevent excessive re-renders during typing
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  
  // Initialize sortConfig from cache
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>(() => {
    const cached = loadPreferences();
    return {
      key: cached.sortKey,
      direction: cached.sortDirection
    };
  });


  // Persist debounced searchTerm to URL and cache
  useEffect(() => {
    const latest = new URLSearchParams(window.location.search);
    if (debouncedSearchTerm) {
      latest.set("q", debouncedSearchTerm);
    } else {
      latest.delete("q");
    }
    setSearchParams(latest, { replace: true });
    savePreferences({ searchTerm: debouncedSearchTerm });
  }, [debouncedSearchTerm, setSearchParams]);

  // Filter notifications based on debounced search term
  const filteredNotifications = useMemo(() => {
    return notifications.filter(notification => {
      const searchTermLower = debouncedSearchTerm.toLowerCase();
      return (
        ((notification.vo_number || "").toLowerCase().includes(searchTermLower)) ||
        notification.message.toLowerCase().includes(searchTermLower) ||
        notification.type.toLowerCase().includes(searchTermLower) ||
        // Add additional search fields for better matching
        (notification.exact_order_number || "").toLowerCase().includes(searchTermLower) ||
        notification.user_name.toLowerCase().includes(searchTermLower)
      );
    });
  }, [notifications, debouncedSearchTerm]);

  // Sort filtered notifications
  const sortedNotifications = useMemo(() => {
    return [...filteredNotifications].sort((a, b) => {
      const aValue = a[sortConfig.key as keyof SupabaseNotification];
      const bValue = b[sortConfig.key as keyof SupabaseNotification];
      
      if (sortConfig.key === "created_at" || sortConfig.key === "updated_at") {
        return sortConfig.direction === "asc" 
          ? new Date(aValue as string).getTime() - new Date(bValue as string).getTime()
          : new Date(bValue as string).getTime() - new Date(aValue as string).getTime();
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
      }
      
      return 0;
    });
  }, [filteredNotifications, sortConfig]);

  // Save sortConfig to cache whenever it changes
  useEffect(() => {
    savePreferences({
      sortKey: sortConfig.key,
      sortDirection: sortConfig.direction
    });
  }, [sortConfig]);

  // Function to request sorting
  const requestSort = (key: string) => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  return {
    searchTerm,
    setSearchTerm,
    filteredNotifications,
    sortedNotifications,
    sortConfig,
    requestSort
  };
};
